import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  UserPlus, 
  FileText, 
  Settings, 
  LogOut,
  Crown,
  MessageSquare,
  Clapperboard,
  Wifi,
  Play,
  Smartphone,
  Megaphone,
  Bell,
  Link as LinkIcon,
  Tablet,
  ShieldAlert,
  Banknote,
  ShoppingCart
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useCms } from '../../contexts/CmsContext';
import { useTheme } from '../../contexts/ThemeContext';
import DynamicLogo from '../ui/DynamicLogo';
import UserCredits from '../ui/UserCredits';
import { apiService } from '../../services/api';
import { isCmsSellerGroupUser } from '../../utils/cmsSellerGroups';

interface CustomSidebarProps {
  className?: string;
}

const CustomSidebar: React.FC<CustomSidebarProps> = ({ className }) => {
  const {
    user,
    logout,
    isAdmin,
    isSocio,
    isMaster,
    isReseller,
    isSubReseller,
    canViewResellers,
    canViewSettings
  } = useAuth();
  const { currentCms } = useCms();
  const { theme } = useTheme();
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Extrair slug da URL atual - sempre retorna um slug
  const getCurrentSlug = () => {
    const path = location.pathname;
    
    // Extrair o primeiro segmento após /
    const segments = path.split('/');
    const possibleSlug = segments[1];
    
    // Se não tiver slug ou for uma rota especial, usar u9ion
    if (!possibleSlug || possibleSlug === '' || possibleSlug === 'login') {
      return 'u9ion';
    }
    
    return possibleSlug;
  };

  const currentSlug = getCurrentSlug();

  /** Venda automática ativada no CMS do master (mesma fonte que GET /credit-purchases/catalog). */
  const [masterCreditSalesEnabled, setMasterCreditSalesEnabled] = useState<boolean | null>(null);
  const [storePaymentsVisible, setStorePaymentsVisible] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) {
      setStorePaymentsVisible(null);
      return;
    }
    let cancelled = false;
    setStorePaymentsVisible(null);
    void (async () => {
      try {
        const res = await apiService.getStorePaymentsAccess();
        if (cancelled) return;
        setStorePaymentsVisible(Boolean(res?.success && res?.data?.canView));
      } catch {
        if (!cancelled) setStorePaymentsVisible(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, currentSlug]);

  useEffect(() => {
    if (!user || !isCmsSellerGroupUser(user)) {
      setMasterCreditSalesEnabled(null);
      return;
    }
    let cancelled = false;
    setMasterCreditSalesEnabled(null);
    void (async () => {
      try {
        const res = await apiService.getCreditPurchaseCatalog();
        if (cancelled) return;
        if (res?.success && res.data && typeof res.data.creditSalesEnabled === 'boolean') {
          setMasterCreditSalesEnabled(Boolean(res.data.creditSalesEnabled));
        } else {
          setMasterCreditSalesEnabled(false);
        }
      } catch {
        if (!cancelled) setMasterCreditSalesEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, currentSlug]);

  const showBuyCreditsUi =
    Boolean(user && isCmsSellerGroupUser(user)) && masterCreditSalesEnabled === true;

  const getNavItems = () => {
    const baseItems = [
      {
        name: 'Dashboard',
        href: `/${currentSlug}/dashboard`,
        icon: Home,
        show: true
      },
      {
        name: 'Usuários',
        href: `/${currentSlug}/users`,
        icon: Users,
        show: isAdmin() || isSocio() || isMaster() // Admin, Sócio e Master podem gerenciar usuários CMS
      },
      {
        name: 'Avisos Fixos',
        href: `/${currentSlug}/announcements`,
        icon: Megaphone,
        show: isAdmin() // Apenas admin pode gerenciar avisos fixos no dashboard
      },
      {
        name: 'Notificações',
        href: `/${currentSlug}/notifications`,
        icon: Bell,
        show: isAdmin() // Apenas admin pode gerenciar notificações em tempo real
      },
      {
        name: 'Clientes',
        href: `/${currentSlug}/clients`,
        icon: UserPlus,
        show: true // Todos podem ver Clientes
      },
      {
        name: 'Clientes Online',
        href: `/${currentSlug}/online-clients`,
        icon: Wifi,
        show: true // Todos podem ver Clientes Online
      },
      {
        name: 'Dispositivos',
        href: `/${currentSlug}/device-management`,
        icon: Tablet,
        show: isReseller() || isSocio() || isMaster() || isAdmin() // Revendedores e acima podem gerenciar dispositivos
      },
      {
        name: 'Revendedores',
        href: `/${currentSlug}/resellers`,
        icon: Crown,
        show: canViewResellers() // Sócios e Masters podem ver Revendedores
      },
      {
        name: 'Tickets',
        href: `/${currentSlug}/tickets`,
        icon: MessageSquare,
        show: isReseller() || isSocio() || isMaster() || isAdmin() // Todos os revendedores e acima podem ver Tickets
      },
      {
        name: 'Solicitações',
        href: `/${currentSlug}/content-requests`,
        icon: Clapperboard,
        show: isReseller() || isSocio() || isMaster() || isAdmin() || isSubReseller()
      },
      {
        name: 'VODs',
        href: `/${currentSlug}/vods`,
        icon: Play,
        show: true // Todos podem ver VODs
      },
      {
        name: 'Logs',
        href: `/${currentSlug}/logs`,
        icon: FileText,
        show: isAdmin() || isSocio() || isMaster() || isReseller()
      },
      {
        name: 'Aplicativos',
        href: `/${currentSlug}/app-management`,
        icon: Smartphone,
        show: isAdmin() // Apenas admin pode gerenciar aplicativos
      },
      {
        name: 'Links Encurtados',
        href: `/${currentSlug}/short-urls`,
        icon: LinkIcon,
        show: true // Todos os usuários podem gerenciar seus próprios links
      },
      {
        name: 'Pagamentos loja',
        href: `/${currentSlug}/store-payments`,
        icon: Banknote,
        show: storePaymentsVisible === true
      },
      {
        name: 'Comprar créditos',
        href: `/${currentSlug}/buy-credits`,
        icon: ShoppingCart,
        show: showBuyCreditsUi
      },
      {
        name: 'Configurações',
        href: `/${currentSlug}/settings`,
        icon: Settings,
        show: canViewSettings() // Sócios, Masters, Revendedores e Admins podem ver Configurações
      },
      {
        name: 'Segurança',
        href: `/${currentSlug}/security-management`,
        icon: ShieldAlert,
        show: isAdmin() // APENAS admin pode gerenciar segurança
      }
    ];

    const visibleItems = baseItems.filter(item => item.show);
    
    return visibleItems;
  };


  const navItems = getNavItems();

  // Usar cores do tema se disponível, senão usar cores do CMS
  const sidebarColors = theme?.colors || currentCms?.colors || {
    primary: '#3B82F6',
    secondary: '#1E40AF'
  };
  
  return (
    <div 
      className={`h-screen flex flex-col relative overflow-hidden sidebar-theme ${className}`}
      style={{ 
        background: `linear-gradient(135deg, ${sidebarColors.primary} 0%, ${sidebarColors.secondary} 100%)`
      }}
    >
      {/* Efeito de brilho sutil */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none"></div>
      {/* Header com Logo e Nome do CMS */}
      <div className="p-3 sm:p-4 lg:p-6 border-b border-white/20 bg-black/10 relative z-10 flex-shrink-0">
        <div className="flex flex-col items-center justify-center space-y-2 sm:space-y-3 lg:space-y-4 w-full">
          {/* Logo Centralizada */}
          <div className="flex justify-center w-full">
            <DynamicLogo 
              className="w-16 h-16 sm:w-20 sm:h-20 lg:w-32 lg:h-32"
              size="lg"
              showText={false}
            />
          </div>
          {/* Nome e Slug Centralizados */}
          <div className="text-center w-full">
            <h1 className="text-white font-bold text-base sm:text-lg lg:text-2xl mb-0.5 sm:mb-1">
              {theme?.cmsName || currentCms?.cmsName || 'CMS'}
            </h1>
            <p className="text-white/80 text-xs sm:text-sm">
              {theme?.cmsSlug || currentCms?.slug || 'painel'}
            </p>
          </div>
        </div>
      </div>

      {/* Informações do Usuário */}
      {user && (
        <div className="p-2 sm:p-3 lg:p-4 border-b border-white/20 bg-black/5 relative z-10 flex-shrink-0">
          {/*
            Atalho só quando o master dono do CMS (slug atual) tiver `creditSales.enabled` — mesma regra do catálogo.
            Grupos de venda: sócio, master, revendedor, sub-revendedor (não admin).
          */}
          <div className="flex gap-2 w-full items-stretch">
            <div className="flex-1 min-w-0">
              <UserCredits
                credits={user.credits}
                size="sm"
                className="w-full"
                autoRefresh={true}
                refreshInterval={60000}
              />
            </div>
            {showBuyCreditsUi && (
              <div className="flex shrink-0 items-center">
                <Link
                  to={`/${currentSlug}/buy-credits`}
                  title="Comprar créditos"
                  className="flex h-9 w-9 items-center justify-center rounded-lg border-2 border-white/20 bg-white/10 text-white hover:bg-emerald-500/30 hover:border-emerald-300/40 transition-colors"
                >
                  <ShoppingCart className="h-4 w-4" aria-hidden />
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Botão Sair - Movido para cima para melhor visibilidade */}
      <div className="p-2 sm:p-3 lg:p-4 border-b border-white/20 bg-black/5 relative z-10 flex-shrink-0">
        <button
          onClick={logout}
          className="w-full flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 sm:py-2 rounded-lg text-white/80 hover:bg-red-500/20 hover:text-white hover:shadow-md transition-all duration-200 group min-h-[40px] sm:min-h-[44px]"
        >
          <LogOut className="w-5 h-5 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="font-medium text-xs sm:text-sm lg:text-base">Sair</span>
        </button>
      </div>

      {/* Navegação */}
      <nav 
        className="flex-1 overflow-y-auto overflow-x-hidden p-2 sm:p-3 lg:p-4 space-y-0.5 sm:space-y-1 lg:space-y-2 relative z-10 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent min-h-0"
        style={{
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          maxHeight: '100%'
        }}
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 sm:py-2 rounded-lg transition-all duration-200 group min-h-[40px] sm:min-h-[44px] ${
                isActive(item.href)
                  ? 'bg-white/25 text-white shadow-lg transform scale-105'
                  : 'text-white/80 hover:bg-white/15 hover:text-white hover:shadow-md hover:transform hover:scale-102'
              }`}
            >
              <Icon className="w-5 h-5 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="font-medium text-xs sm:text-sm lg:text-base truncate">{item.name}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default CustomSidebar;
